import { FC } from 'react'

import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { Box, Typography, alpha } from '@mui/material'

/** Swatch shape: the delivered fill, the target goalpost, or the uncovered gap hatch. */
export type GapLegendVariant = 'fill' | 'marker' | 'hatch'

interface GapLegendItem {
  /** Glyph shape, drawn to match its on-bar counterpart in {@link MotivationGapBar}. */
  variant: GapLegendVariant
  color: string
  label: string
  /**
   * Optional icon (e.g. the game's app icon) shown in place of a `marker`
   * swatch's dot, matching the bar's goalpost. Ignored for other variants.
   */
  icon?: string
  /**
   * When a `marker` swatch has no `icon` (e.g. a genre), draw the generic
   * category badge instead of the plain dot, matching the bar marker. Ignored
   * for other variants.
   */
  genericIcon?: boolean
}

interface MotivationGapLegendProps {
  /** Legend entries rendered left-to-right; ordered to match the bar (fill, hatch, then marker). */
  items: GapLegendItem[]
}

/**
 * Section-level legend for the {@link MotivationGapBar} bullet bar: one swatch +
 * label per signal (a `marker` goalpost, a solid `fill`, and a `hatch` gap band)
 * drawn to match their on-bar counterparts. Rendered once above a column of gap
 * bars so each card stays label-free and scannable. Shared by the single-game
 * blind-spots diagnosis and the cross-entity comparison diagnosis so the two
 * surfaces read as one system.
 */
export const MotivationGapLegend: FC<MotivationGapLegendProps> = ({ items }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
    {items.map((item) => (
      <SignalLegendItem key={item.label} variant={item.variant} color={item.color} label={item.label} icon={item.icon} genericIcon={item.genericIcon} />
    ))}
  </Box>
)

interface SignalLegendItemProps {
  variant: GapLegendVariant
  color: string
  label: string
  icon?: string
  genericIcon?: boolean
}

/** One swatch + label entry of the section-level legend, matching the gap bar. */
const SignalLegendItem: FC<SignalLegendItemProps> = ({ variant, color, label, icon, genericIcon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
    <LegendSwatch variant={variant} color={color} icon={icon} genericIcon={genericIcon} />
    <Typography
      variant="caption"
      sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1 }}
    >
      {label}
    </Typography>
  </Box>
)

/** The coloured glyph of a legend item, drawn to match its on-bar counterpart. */
const LegendSwatch: FC<{ variant: GapLegendVariant; color: string; icon?: string; genericIcon?: boolean }> = ({ variant, color, icon, genericIcon }) => {
  if (variant === 'marker') {
    // When an icon is supplied, mirror the bar's goalpost: the game's app icon
    // as a rounded-square chip ringed in the marker colour.
    if (icon) {
      return (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{
            width: 15,
            height: 15,
            borderRadius: '4px',
            objectFit: 'cover',
            flexShrink: 0,
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: color,
          }}
        />
      )
    }
    // No app icon (e.g. a genre): the generic category badge, ringed to match
    // the bar marker's genre fallback.
    if (genericIcon) {
      return (
        <Box
          sx={{
            width: 15,
            height: 15,
            borderRadius: '4px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: color,
          }}
        >
          <CategoryRoundedIcon sx={{ fontSize: 9 }} />
        </Box>
      )
    }
    return (
      <Box sx={{ position: 'relative', width: 10, height: 12, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, width: '2.5px', bgcolor: color, borderRadius: '999px' }} />
        <Box
          sx={{ position: 'absolute', top: 0, width: 8, height: 8, borderRadius: '50%', bgcolor: color, border: '2px solid', borderColor: 'background.paper' }}
        />
      </Box>
    )
  }
  return (
    <Box
      sx={{
        width: 16,
        // Matches the marker icon's height so all three swatches sit on one line.
        height: 15,
        borderRadius: '4px',
        flexShrink: 0,
        ...(variant === 'fill'
          ? { bgcolor: color }
          : {
              backgroundImage: `repeating-linear-gradient(135deg, ${alpha(color, 0.5)} 0, ${alpha(color, 0.5)} 3px, ${alpha(color, 0.16)} 3px, ${alpha(
                color,
                0.16
              )} 6px)`,
            }),
      }}
    />
  )
}
